/**
 * Ads Manager - Multi-Platform Ad Management Dashboard
 * 
 * Full-featured ads management view with:
 * - Campaign → Ad Set → Ad hierarchy
 * - Column customization & saved presets
 * - Filters, pagination, totals row
 * - Platform connections management
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Users, 
  Calendar,
  RefreshCw,
  Download,
  Settings,
  Target,
  Zap,
  ShoppingCart,
  Filter,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Columns3,
  Save,
  MoreHorizontal,
  Link2,
  Link2Off,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Eye,
  Pause,
  Play,
  BarChart3,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import ConnectionsModal from '../../components/marketing/ConnectionsModal';

const API = process.env.REACT_APP_BACKEND_URL;

// All available columns for the ads table
const ALL_COLUMNS = [
  { id: 'name', label: 'Name', required: true, category: 'info' },
  { id: 'status', label: 'Status', category: 'info' },
  { id: 'platform', label: 'Platform', category: 'info' },
  { id: 'objective', label: 'Objective', category: 'info' },
  { id: 'impressions', label: 'Impressions', category: 'delivery', format: 'number' },
  { id: 'clicks', label: 'Clicks', category: 'delivery', format: 'number' },
  { id: 'ctr', label: 'CTR', category: 'delivery', format: 'percent' },
  { id: 'spend', label: 'Spend', category: 'cost', format: 'currency' },
  { id: 'cpc', label: 'CPC', category: 'cost', format: 'currency' },
  { id: 'cpm', label: 'CPM', category: 'cost', format: 'currency' },
  { id: 'conversions', label: 'Conversions', category: 'results', format: 'number' },
  { id: 'leads', label: 'Leads', category: 'results', format: 'number' },
  { id: 'purchases', label: 'Purchases', category: 'results', format: 'number' },
  { id: 'cpa', label: 'CPA', category: 'cost', format: 'currency' },
  { id: 'conversion_value', label: 'Conv. Value', category: 'results', format: 'currency' },
  { id: 'roas', label: 'ROAS', category: 'results', format: 'roas' },
];

// Default visible columns
const DEFAULT_COLUMNS = ['name', 'status', 'platform', 'impressions', 'clicks', 'spend', 'leads', 'cpa', 'roas'];

// Date range options
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'last_14', label: 'Last 14 Days' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'ytd', label: 'Year to Date' },
];

// Status badge colors
const STATUS_COLORS = {
  active: 'bg-green-500/10 text-green-600 border-green-500/20',
  paused: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  ended: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  draft: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
};

// Platform icons/colors
const PLATFORM_CONFIG = {
  meta: { label: 'Meta', color: 'bg-blue-500', icon: '📘' },
  google: { label: 'Google', color: 'bg-red-500', icon: '🔍' },
  microsoft: { label: 'Microsoft', color: 'bg-cyan-500', icon: '🪟' },
  linkedin: { label: 'LinkedIn', color: 'bg-blue-700', icon: '💼' },
  tiktok: { label: 'TikTok', color: 'bg-black', icon: '🎵' },
};

// Format cell values
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'currency':
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'number':
      return value.toLocaleString('en-US');
    case 'percent':
      return `${value.toFixed(2)}%`;
    case 'roas':
      return `${value.toFixed(2)}x`;
    default:
      return value;
  }
};

// Calculate date range
const getDateRange = (rangeValue) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start, end;
  
  switch (rangeValue) {
    case 'today':
      start = end = today;
      break;
    case 'yesterday':
      start = end = new Date(today.getTime() - 86400000);
      break;
    case 'last_7':
      start = new Date(today.getTime() - 7 * 86400000);
      end = today;
      break;
    case 'last_14':
      start = new Date(today.getTime() - 14 * 86400000);
      end = today;
      break;
    case 'last_30':
      start = new Date(today.getTime() - 30 * 86400000);
      end = today;
      break;
    case 'this_month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = today;
      break;
    case 'last_month':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case 'ytd':
      start = new Date(now.getFullYear(), 0, 1);
      end = today;
      break;
    default:
      start = new Date(today.getTime() - 30 * 86400000);
      end = today;
  }
  
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
};


// Campaign Children Section - Shows ad sets/ads within a campaign detail modal
const CampaignChildrenSection = ({ campaignId, campaignName, childLevel, token, dateRange, onViewChild }) => {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChild, setExpandedChild] = useState(null); // Which ad set is expanded
  const [grandchildren, setGrandchildren] = useState({}); // {adsetId: [ads]}
  const [loadingGrandchildren, setLoadingGrandchildren] = useState(null);

  useEffect(() => {
    const fetchChildren = async () => {
      setLoading(true);
      try {
        const dates = getDateRange(dateRange);
        const response = await axios.get(
          `${API}/api/marketing/ads/report/demo`,
          {
            params: { level: childLevel, start_date: dates.start, end_date: dates.end },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const allChildren = response.data.data || [];
        const filtered = allChildren.filter(c => c.campaign_id === campaignId);
        setChildren(filtered);
      } catch (e) {
        console.error('Failed to fetch children:', e);
      } finally {
        setLoading(false);
      }
    };
    if (campaignId) fetchChildren();
  }, [campaignId, childLevel, dateRange, token]);

  const fetchAdsForAdSet = async (adsetId) => {
    if (grandchildren[adsetId]) return; // Already loaded
    setLoadingGrandchildren(adsetId);
    try {
      const dates = getDateRange(dateRange);
      const response = await axios.get(
        `${API}/api/marketing/ads/report/demo`,
        {
          params: { level: 'ad', start_date: dates.start, end_date: dates.end },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const allAds = response.data.data || [];
      const filtered = allAds.filter(a => a.adgroup_id === adsetId || a.campaign_id === campaignId);
      // If filtering by adgroup didn't work, try matching by campaign
      const adsForSet = filtered.length > 0 ? filtered : allAds.filter(a => a.campaign_id === campaignId);
      setGrandchildren(prev => ({ ...prev, [adsetId]: adsForSet }));
    } catch (e) {
      setGrandchildren(prev => ({ ...prev, [adsetId]: [] }));
    } finally {
      setLoadingGrandchildren(null);
    }
  };

  const handleChildClick = (child) => {
    if (child.child_count > 0) {
      // Toggle expand to show ads
      if (expandedChild === child.id) {
        setExpandedChild(null);
      } else {
        setExpandedChild(child.id);
        fetchAdsForAdSet(child.id);
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {childLevel === 'adgroup' ? 'Ad Sets' : 'Ads'} in this Campaign
        </h3>
        <div className="animate-pulse space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />)}
        </div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {childLevel === 'adgroup' ? 'Ad Sets' : 'Ads'} in this Campaign
        </h3>
        <p className="text-xs text-muted-foreground italic">No {childLevel === 'adgroup' ? 'ad sets' : 'ads'} found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        {childLevel === 'adgroup' ? 'Ad Sets' : 'Ads'} ({children.length})
      </h3>
      <div className="border dark:border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/50 border-b dark:border-slate-700">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-2 py-2 text-center font-medium text-muted-foreground">Status</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground">Spend</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground">Impressions</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground">Clicks</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground">Leads</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground">CPA</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {children.map((child, idx) => (
              <React.Fragment key={child.id || idx}>
                <tr 
                  className="border-b dark:border-slate-700/50 hover:bg-muted/30 cursor-pointer"
                  onClick={() => handleChildClick(child)}
                >
                  <td className="px-3 py-2 font-medium">
                    <div className="flex items-center gap-1">
                      {child.child_count > 0 && (
                        expandedChild === child.id 
                          ? <ChevronDown className="h-3 w-3 text-primary" />
                          : <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      )}
                      {child.name}
                      {child.child_count > 0 && (
                        <Badge variant="secondary" className="ml-1 text-[8px] px-1 py-0">{child.child_count} ads</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <Badge 
                      variant="outline" 
                      className={`text-[9px] px-1 py-0 cursor-pointer hover:opacity-80 ${STATUS_COLORS[child.status] || ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newStatus = child.status === 'active' ? 'paused' : 'active';
                        axios.put(
                          `${API}/api/marketing/ads/campaigns/${child.id}`,
                          { status: newStatus },
                          { headers: { Authorization: `Bearer ${token}` } }
                        ).then(() => {
                          // Update local children state
                          setChildren(prev => prev.map(c => c.id === child.id ? { ...c, status: newStatus } : c));
                          toast.success(`Ad set ${newStatus === 'paused' ? 'paused' : 'activated'}`);
                        }).catch(() => toast.error('Failed'));
                      }}
                      title="Click to toggle"
                    >
                      {child.status}
                    </Badge>
                  </td>
                  <td className="px-2 py-2 text-right">{formatValue(child.spend, 'currency')}</td>
                  <td className="px-2 py-2 text-right">{formatValue(child.impressions, 'number')}</td>
                  <td className="px-2 py-2 text-right">{formatValue(child.clicks, 'number')}</td>
                  <td className="px-2 py-2 text-right">{formatValue(child.leads, 'number')}</td>
                  <td className="px-2 py-2 text-right">{formatValue(child.cpa, 'currency')}</td>
                  <td className="px-2 py-2 text-right">{formatValue(child.roas, 'roas')}</td>
                </tr>
                {/* Expanded ads within this ad set */}
                {expandedChild === child.id && (
                  loadingGrandchildren === child.id ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-2 text-muted-foreground">
                        <RefreshCw className="h-3 w-3 animate-spin inline mr-1" /> Loading ads...
                      </td>
                    </tr>
                  ) : (grandchildren[child.id] || []).map((ad, adIdx) => (
                    <tr 
                      key={ad.id || adIdx}
                      className="bg-muted/10 border-b dark:border-slate-700/30 hover:bg-muted/20 cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); onViewChild?.(ad); }}
                    >
                      <td className="px-3 py-1.5 pl-8 text-muted-foreground">
                        <span className="text-primary/30 mr-1">└</span>
                        {ad.name}
                        {ad.creative_type && (
                          <Badge variant="outline" className="ml-1 text-[7px] px-1 py-0">{ad.creative_type}</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Badge 
                          variant="outline" 
                          className={`text-[9px] px-1 py-0 cursor-pointer hover:opacity-80 ${STATUS_COLORS[ad.status] || ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newStatus = ad.status === 'active' ? 'paused' : 'active';
                            axios.put(
                              `${API}/api/marketing/ads/campaigns/${ad.id}`,
                              { status: newStatus },
                              { headers: { Authorization: `Bearer ${token}` } }
                            ).then(() => {
                              setGrandchildren(prev => ({
                                ...prev,
                                [child.id]: (prev[child.id] || []).map(a => a.id === ad.id ? { ...a, status: newStatus } : a)
                              }));
                              toast.success(`Ad ${newStatus === 'paused' ? 'paused' : 'activated'}`);
                            }).catch(() => toast.error('Failed'));
                          }}
                          title="Click to toggle"
                        >
                          {ad.status}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">{formatValue(ad.spend, 'currency')}</td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">{formatValue(ad.impressions, 'number')}</td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">{formatValue(ad.clicks, 'number')}</td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">{formatValue(ad.leads, 'number')}</td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">{formatValue(ad.cpa, 'currency')}</td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">{formatValue(ad.roas, 'roas')}</td>
                    </tr>
                  ))
                )}
              </React.Fragment>
            ))}
            {/* Totals row */}
            <tr className="bg-muted/30 font-semibold border-t dark:border-slate-600">
              <td className="px-3 py-2">Total ({children.length})</td>
              <td className="px-2 py-2"></td>
              <td className="px-2 py-2 text-right">{formatValue(children.reduce((s, c) => s + (c.spend || 0), 0), 'currency')}</td>
              <td className="px-2 py-2 text-right">{formatValue(children.reduce((s, c) => s + (c.impressions || 0), 0), 'number')}</td>
              <td className="px-2 py-2 text-right">{formatValue(children.reduce((s, c) => s + (c.clicks || 0), 0), 'number')}</td>
              <td className="px-2 py-2 text-right">{formatValue(children.reduce((s, c) => s + (c.leads || 0), 0), 'number')}</td>
              <td className="px-2 py-2 text-right">-</td>
              <td className="px-2 py-2 text-right">-</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};


const AdsManager = () => {
  const { token } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [dateRange, setDateRange] = useState('last_30');
  const [level, setLevel] = useState('campaign'); // campaign, adgroup, ad
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [marketFilter, setMarketFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'spend', direction: 'desc' });
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  const [connections, setConnections] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedRow, setSelectedRow] = useState(null); // For detail modal
  const [childData, setChildData] = useState({}); // {campaignId: [child rows]}
  const [loadingChildren, setLoadingChildren] = useState(new Set());
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Presets
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  // Fetch child items (ad sets for campaigns, ads for ad sets)
  const fetchChildren = useCallback(async (parentId, childLevel) => {
    if (childData[parentId]) return; // Already loaded
    setLoadingChildren(prev => new Set([...prev, parentId]));
    try {
      const dates = getDateRange(dateRange);
      // Try demo endpoint (same structure, different level)
      const response = await axios.get(
        `${API}/api/marketing/ads/report/demo`,
        {
          params: {
            level: childLevel,
            start_date: dates.start,
            end_date: dates.end,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const allChildren = response.data.data || [];
      // Filter to children of this parent
      const children = allChildren.filter(c => 
        childLevel === 'adgroup' ? c.campaign_id === parentId :
        childLevel === 'ad' ? c.adgroup_id === parentId || c.campaign_id === parentId :
        false
      );
      setChildData(prev => ({ ...prev, [parentId]: children }));
    } catch (e) {
      console.error('Failed to fetch children:', e);
      setChildData(prev => ({ ...prev, [parentId]: [] }));
    } finally {
      setLoadingChildren(prev => { const next = new Set(prev); next.delete(parentId); return next; });
    }
  }, [childData, dateRange, token]);

  // Create campaign state
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [newCampaignData, setNewCampaignData] = useState({
    name: '', platform: 'meta', objective: 'leads', daily_budget: '', market: '', status: 'active'
  });
  const [showAddAd, setShowAddAd] = useState(false);
  const [newAdData, setNewAdData] = useState({ name: '', creative_type: 'image' });
  const [addAdTargetCampaign, setAddAdTargetCampaign] = useState(null);

  const handleCreateCampaign = async () => {
    if (!newCampaignData.name.trim()) return;
    try {
      await axios.put(
        `${API}/api/marketing/ads/campaigns/new-${Date.now()}`,
        { name: newCampaignData.name, status: 'active', budget_daily: parseFloat(newCampaignData.daily_budget) || 0, market: newCampaignData.market },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Add to local data immediately
      setData(prev => [{
        id: `new-${Date.now()}`,
        name: newCampaignData.name,
        platform: newCampaignData.platform,
        status: 'active',
        objective: newCampaignData.objective,
        daily_budget: parseFloat(newCampaignData.daily_budget) || 0,
        impressions: 0, clicks: 0, spend: 0, leads: 0, cpa: 0, roas: 0,
        conversions: 0, conversion_value: 0, child_count: 0,
      }, ...prev]);
      setShowCreateCampaign(false);
      setNewCampaignData({ name: '', platform: 'meta', objective: 'leads', daily_budget: '', market: '', status: 'active' });
      toast.success('Campaign created (demo mode)');
    } catch (e) {
      toast.error('Failed to create campaign');
    }
  };

  // Fetch ads data - tries real data first, then demo if available
  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const dates = getDateRange(dateRange);
      
      // First try to get real data via POST endpoint
      try {
        const response = await axios.post(
          `${API}/api/marketing/ads/report`,
          {
            level,
            start_date: dates.start,
            end_date: dates.end,
            platforms: platformFilter !== 'all' ? [platformFilter] : undefined,
            sort_by: sortConfig.key || 'spend',
            sort_order: sortConfig.direction || 'desc',
            page,
            page_size: pageSize,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const realData = response.data.data || [];
        if (realData.length > 0) {
          setData(realData);
          setTotals(response.data.totals || {});
          return; // Success with real data
        }
        // No real data - fall through to demo
      } catch (realDataErr) {
        // Real data not available, try demo endpoint
        console.log('Real data not available, checking demo mode:', realDataErr?.response?.status);
      }
      
      // Fallback to demo endpoint
      const response = await axios.get(
        `${API}/api/marketing/ads/report/demo`,
        {
          params: {
            level,
            start_date: dates.start,
            end_date: dates.end,
            platform: platformFilter !== 'all' ? platformFilter : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setData(response.data.data || []);
      setTotals(response.data.totals || {});
    } catch (err) {
      console.error('Error fetching ads data:', err);
      const errorDetail = err.response?.data?.detail || 'Failed to load ads data';
      
      // Check if it's a "no data" error
      if (errorDetail.includes('Demo mode is disabled') || errorDetail.includes('no real data')) {
        setError('No ad data available. Connect your ad platforms in Settings to sync real campaign data.');
      } else {
        setError(errorDetail);
      }
      
      // Clear data
      setData([]);
      setTotals({});
    } finally {
      setLoading(false);
    }
  }, [token, dateRange, level, platformFilter, statusFilter, sortConfig, page, pageSize]);

  // Fetch connections
  const fetchConnections = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(
        `${API}/api/marketing/ads/connections`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConnections(response.data || []);
    } catch (err) {
      console.error('Error fetching connections:', err);
      // Don't mock connections anymore - show empty state
      setConnections([]);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchConnections();
  }, [fetchData, fetchConnections]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...data];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(row => 
        row.name?.toLowerCase().includes(term) ||
        row.platform?.toLowerCase().includes(term)
      );
    }
    
    // Market filter (match campaign name containing market name)
    if (marketFilter) {
      const marketNames = {
        'jacksonville': ['jacksonville', 'jax', 'northeast fl'],
        'orlando': ['orlando', 'orl'],
        'tampa': ['tampa', 'tam'],
        'west-palm': ['west palm', 'south florida', 'wpb'],
        'naples': ['naples', 'fort myers', 'ft. myers', 'ft myers', 'nfm'],
        'gainesville': ['gainesville', 'gnv'],
      };
      const terms = marketNames[marketFilter] || [marketFilter];
      result = result.filter(row => {
        const name = (row.name || '').toLowerCase();
        return terms.some(t => name.includes(t));
      });
    }
    
    // Sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] ?? 0;
        const bVal = b[sortConfig.key] ?? 0;
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        return (aVal > bVal ? 1 : -1) * direction;
      });
    }
    
    return result;
  }, [data, searchTerm, marketFilter, sortConfig]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    const column = ALL_COLUMNS.find(c => c.id === columnId);
    if (column?.required) return;
    
    setVisibleColumns(prev => 
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Save preset
  const savePreset = () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }
    
    const newPreset = {
      id: Date.now().toString(),
      name: presetName,
      columns: visibleColumns,
      level,
    };
    
    setPresets(prev => [...prev, newPreset]);
    setShowSavePreset(false);
    setPresetName('');
    toast.success('Preset saved');
  };

  // Load preset
  const loadPreset = (preset) => {
    setVisibleColumns(preset.columns);
    setLevel(preset.level);
    toast.success(`Loaded preset: ${preset.name}`);
  };

  // Get visible columns config
  const visibleColumnConfigs = useMemo(() => 
    ALL_COLUMNS.filter(col => visibleColumns.includes(col.id)),
    [visibleColumns]
  );

  // Connection status summary
  const connectionSummary = useMemo(() => {
    const connected = connections.filter(c => c.status === 'connected').length;
    return { connected, total: connections.length };
  }, [connections]);

  // Show data if we have demo data OR connected platforms
  const hasData = data.length > 0 || connectionSummary.connected > 0;

  return (
    <Layout>
      <div className="space-y-4 p-4 md:p-6" data-testid="ads-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Ads Manager</h1>
            <p className="text-sm text-muted-foreground">
              Manage campaigns across all ad platforms
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* + New Campaign */}
            <Button
              size="sm"
              onClick={() => setShowCreateCampaign(true)}
              className="gap-1"
              data-testid="new-campaign-btn"
            >
              <Plus className="h-4 w-4" />
              New Campaign
            </Button>
            
            {/* Connection Status */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConnectionsModal(true)}
              className="gap-2"
              data-testid="connections-btn"
            >
              <Link2 className="h-4 w-4" />
              <span className="hidden sm:inline">Connections</span>
              <Badge variant={connectionSummary.connected > 0 ? "default" : "secondary"} className="ml-1">
                {connectionSummary.connected}/{connectionSummary.total || 2}
              </Badge>
            </Button>
            
            {/* Refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            {/* Settings */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConnectionsModal(true)}
              data-testid="settings-btn"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">Total Spend</div>
            <div className="text-xl font-bold">
              {hasData ? formatValue(totals.spend, 'currency') : '-'}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">Impressions</div>
            <div className="text-xl font-bold">
              {hasData ? formatValue(totals.impressions, 'number') : '-'}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">Clicks</div>
            <div className="text-xl font-bold">
              {hasData ? formatValue(totals.clicks, 'number') : '-'}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">Leads</div>
            <div className="text-xl font-bold">
              {hasData ? formatValue(totals.leads, 'number') : '-'}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">CPA</div>
            <div className="text-xl font-bold">
              {hasData ? formatValue(totals.cpa, 'currency') : '-'}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">ROAS</div>
            <div className="text-xl font-bold">
              {hasData ? formatValue(totals.roas, 'roas') : '-'}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-xs text-muted-foreground">On-Track Pacing</div>
            <div className="text-xl font-bold">
              {hasData ? (() => {
                const now = new Date();
                const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                const dayElapsed = now.getDate();
                const pacingPct = totals.spend > 0 ? Math.round(((totals.spend / dayElapsed) * daysInMonth) / (totals.spend * 1.5) * 100) : 0;
                return `${Math.min(pacingPct, 150)}%`;
              })() : '-'}
            </div>
          </Card>
        </div>

        {/* Connection Banner - show when no platforms connected */}
        {connectionSummary.connected === 0 && !hasData && (
          <Card className="p-4 border-dashed bg-muted/30">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Link2 className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Connect Your Ad Platforms</h3>
                <p className="text-sm text-muted-foreground">
                  Connect Meta or Google Ads to see your campaign performance data
                </p>
              </div>
              <Button onClick={() => setShowConnectionsModal(true)} data-testid="connect-cta">
                <Link2 className="h-4 w-4 mr-2" />
                Connect Platforms
              </Button>
            </div>
          </Card>
        )}

        {hasData && connectionSummary.connected === 0 && (
          <div className="flex items-center gap-2 px-2">
            <Badge variant="outline" className="text-amber-500 border-amber-500 text-xs">Demo Data</Badge>
            <span className="text-xs text-muted-foreground">Showing demo data until platforms are connected</span>
          </div>
        )}

        {/* Filter Bar */}
        <Card className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Level Tabs */}
            <Tabs value={level} onValueChange={setLevel} className="w-auto">
              <TabsList className="h-8">
                <TabsTrigger value="campaign" className="text-xs px-3 h-7" data-testid="level-campaign">
                  Campaigns
                </TabsTrigger>
                <TabsTrigger value="adgroup" className="text-xs px-3 h-7" data-testid="level-adgroup">
                  Ad Sets
                </TabsTrigger>
                <TabsTrigger value="ad" className="text-xs px-3 h-7" data-testid="level-ad">
                  Ads
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="h-6 w-px bg-border" />

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 w-[160px] text-xs"
                data-testid="search-input"
              />
            </div>

            {/* Date Range */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="date-range-select">
                <Calendar className="h-3 w-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map(range => (
                  <SelectItem key={range.value} value={range.value} className="text-xs">
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Platform Filter */}
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="platform-filter">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Platforms</SelectItem>
                {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key} className="text-xs">
                    {config.icon} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[110px] h-8 text-xs" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Status</SelectItem>
                <SelectItem value="active" className="text-xs">Active</SelectItem>
                <SelectItem value="paused" className="text-xs">Paused</SelectItem>
                <SelectItem value="ended" className="text-xs">Ended</SelectItem>
              </SelectContent>
            </Select>

            {/* Market Filter */}
            <Select value={marketFilter || 'all'} onValueChange={(v) => setMarketFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="market-filter-ads">
                <SelectValue placeholder="All Markets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Markets</SelectItem>
                <SelectItem value="jacksonville" className="text-xs">Jacksonville</SelectItem>
                <SelectItem value="orlando" className="text-xs">Orlando</SelectItem>
                <SelectItem value="tampa" className="text-xs">Tampa</SelectItem>
                <SelectItem value="west-palm" className="text-xs">West Palm</SelectItem>
                <SelectItem value="naples" className="text-xs">Naples</SelectItem>
                <SelectItem value="gainesville" className="text-xs">Gainesville</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex-1" />

            {/* Column Picker */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="column-picker">
                  <Columns3 className="h-3 w-3" />
                  Columns
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel className="text-xs">Toggle Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_COLUMNS.map(column => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={visibleColumns.includes(column.id)}
                    onCheckedChange={() => toggleColumn(column.id)}
                    disabled={column.required}
                    className="text-xs"
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowSavePreset(true)} className="text-xs">
                  <Save className="h-3 w-3 mr-2" />
                  Save as Preset
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Presets */}
            {presets.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
                    Presets
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {presets.map(preset => (
                    <DropdownMenuItem
                      key={preset.id}
                      onClick={() => loadPreset(preset)}
                      className="text-xs"
                    >
                      {preset.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Export */}
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="export-btn">
              <Download className="h-3 w-3" />
              Export
            </Button>
          </div>
        </Card>

        {/* Data Table */}
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  {visibleColumnConfigs.map(column => (
                    <TableHead
                      key={column.id}
                      className="text-xs font-medium cursor-pointer hover:bg-muted/80 whitespace-nowrap"
                      onClick={() => handleSort(column.id)}
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {sortConfig.key === column.id && (
                          sortConfig.direction === 'asc' 
                            ? <TrendingUp className="h-3 w-3" />
                            : <TrendingDown className="h-3 w-3" />
                        )}
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnConfigs.length + 1} className="h-32 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnConfigs.length + 1} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <AlertCircle className="h-6 w-6" />
                        <span>{error}</span>
                        <Button variant="outline" size="sm" onClick={fetchData}>
                          Retry
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnConfigs.length + 1} className="h-32 text-center text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        {connectionSummary.connected === 0 && !hasData ? (
                          <>
                            <Link2Off className="h-8 w-8 opacity-50" />
                            <span>Connect Meta or Google Ads to see campaign data</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => setShowConnectionsModal(true)}
                            >
                              Connect Platforms
                            </Button>
                          </>
                        ) : (
                          <>
                            <BarChart3 className="h-8 w-8 opacity-50" />
                            <span>No campaigns found for the selected filters</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, idx) => (
                  <React.Fragment key={row.id || idx}>
                    <TableRow className="hover:bg-muted/30 cursor-pointer" data-testid={`row-${idx}`} 
                      onClick={() => {
                        // Always open the detail modal when clicking a row
                        setSelectedRow(row);
                        // Also fetch children for the modal
                        if (level !== 'ad' && row.child_count > 0) {
                          const childLevel = level === 'campaign' ? 'adgroup' : 'ad';
                          fetchChildren(row.id, childLevel);
                        }
                      }}>
                      {visibleColumnConfigs.map(column => (
                        <TableCell key={column.id} className="text-xs py-2 whitespace-nowrap">
                          {column.id === 'name' ? (
                            <div className="flex items-center gap-2 font-medium">
                              {level !== 'ad' && row.child_count > 0 ? (
                                <span onClick={(e) => {
                                  e.stopPropagation();
                                  const isExpanding = !expandedRows.has(row.id);
                                  setExpandedRows(prev => {
                                    const next = new Set(prev);
                                    if (next.has(row.id)) next.delete(row.id);
                                    else next.add(row.id);
                                    return next;
                                  });
                                  if (isExpanding) fetchChildren(row.id, level === 'campaign' ? 'adgroup' : 'ad');
                                }} className="hover:bg-muted rounded p-0.5">
                                  {expandedRows.has(row.id) ? 
                                    <ChevronDown className="h-4 w-4 text-primary" /> : 
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                </span>
                              ) : level !== 'ad' ? (
                                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-30" />
                              ) : null}
                              {row.name}
                              {row.child_count > 0 && (
                                <Badge variant="secondary" className="text-[9px] px-1 py-0 ml-1">
                                  {row.child_count} {level === 'campaign' ? 'ad sets' : 'ads'}
                                </Badge>
                              )}
                            </div>
                          ) : column.id === 'status' ? (
                            <Badge 
                              variant="outline" 
                              className={`text-[10px] px-1.5 py-0 cursor-pointer hover:opacity-80 ${STATUS_COLORS[row.status] || ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const newStatus = row.status === 'active' ? 'paused' : 'active';
                                // Update in demo mode
                                axios.put(
                                  `${API}/api/marketing/ads/campaigns/${row.id}`,
                                  { status: newStatus },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                ).then(() => {
                                  // Update local state
                                  setData(prev => prev.map(r => r.id === row.id ? { ...r, status: newStatus } : r));
                                  toast.success(`Campaign ${newStatus === 'paused' ? 'paused' : 'activated'}`);
                                }).catch(() => toast.error('Failed to update status'));
                              }}
                              title="Click to toggle status"
                            >
                              {row.status}
                            </Badge>
                          ) : column.id === 'platform' ? (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              {PLATFORM_CONFIG[row.platform]?.icon} {PLATFORM_CONFIG[row.platform]?.label || row.platform}
                            </Badge>
                          ) : (
                            formatValue(row[column.id], column.format)
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="py-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem className="text-xs" onClick={() => setSelectedRow(row)}>
                              <Eye className="h-3 w-3 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-xs">
                              <BarChart3 className="h-3 w-3 mr-2" />
                              View Chart
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {row.status === 'active' ? (
                              <DropdownMenuItem className="text-xs text-yellow-600" onClick={(e) => {
                                e.stopPropagation();
                                axios.put(`${API}/api/marketing/ads/campaigns/${row.id}`, { status: 'paused' }, { headers: { Authorization: `Bearer ${token}` } })
                                  .then(() => { setData(prev => prev.map(r => r.id === row.id ? { ...r, status: 'paused' } : r)); toast.success('Campaign paused'); })
                                  .catch(() => toast.error('Failed'));
                              }}>
                                <Pause className="h-3 w-3 mr-2" />
                                Pause
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem className="text-xs text-green-600" onClick={(e) => {
                                e.stopPropagation();
                                axios.put(`${API}/api/marketing/ads/campaigns/${row.id}`, { status: 'active' }, { headers: { Authorization: `Bearer ${token}` } })
                                  .then(() => { setData(prev => prev.map(r => r.id === row.id ? { ...r, status: 'active' } : r)); toast.success('Campaign activated'); })
                                  .catch(() => toast.error('Failed'));
                              }}>
                                <Play className="h-3 w-3 mr-2" />
                                Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded child rows */}
                    {expandedRows.has(row.id) && (
                      loadingChildren.has(row.id) ? (
                        <TableRow>
                          <TableCell colSpan={visibleColumnConfigs.length + 1} className="py-3 pl-12 text-xs text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <RefreshCw className="h-3 w-3 animate-spin" />
                              Loading {level === 'campaign' ? 'ad sets' : 'ads'}...
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (childData[row.id] || []).length > 0 ? (
                        (childData[row.id] || []).map((child, cIdx) => (
                          <TableRow 
                            key={`${row.id}-child-${cIdx}`} 
                            className="hover:bg-muted/20 bg-muted/5 cursor-pointer border-l-2 border-l-primary/30"
                            onClick={(e) => { e.stopPropagation(); setSelectedRow(child); }}
                          >
                            {visibleColumnConfigs.map(column => (
                              <TableCell key={column.id} className="text-xs py-1.5 whitespace-nowrap">
                                {column.id === 'name' ? (
                                  <div className="flex items-center gap-2 pl-8 text-muted-foreground">
                                    <span className="text-primary/40">└</span>
                                    <span className="font-normal">{child.name}</span>
                                    {child.child_count > 0 && (
                                      <Badge variant="outline" className="text-[8px] px-1 py-0">{child.child_count} ads</Badge>
                                    )}
                                  </div>
                                ) : column.id === 'status' ? (
                                  <Badge 
                                    variant="outline" 
                                    className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[child.status] || ''}`}
                                  >
                                    {child.status}
                                  </Badge>
                                ) : column.id === 'platform' ? (
                                  <span className="text-muted-foreground text-[10px]">
                                    {PLATFORM_CONFIG[child.platform]?.label || child.platform}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">
                                    {formatValue(child[column.id], column.format)}
                                  </span>
                                )}
                              </TableCell>
                            ))}
                            <TableCell className="py-1.5">
                              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={(e) => { e.stopPropagation(); setSelectedRow(child); }}>
                                <Eye className="h-3 w-3 text-muted-foreground" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={visibleColumnConfigs.length + 1} className="py-2 pl-12 text-xs text-muted-foreground italic">
                            No {level === 'campaign' ? 'ad sets' : 'ads'} found
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </React.Fragment>
                  ))
                )}
                
                {/* Totals Row */}
                {!loading && !error && paginatedData.length > 0 && (
                  <TableRow className="bg-muted/50 font-medium border-t-2">
                    {visibleColumnConfigs.map(column => (
                      <TableCell key={column.id} className="text-xs py-2 whitespace-nowrap">
                        {column.id === 'name' ? (
                          <span className="font-semibold">Total ({filteredData.length})</span>
                        ) : column.id === 'status' || column.id === 'platform' || column.id === 'objective' ? (
                          '-'
                        ) : (
                          <span className="font-semibold">
                            {formatValue(totals[column.id], column.format)}
                          </span>
                        )}
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {!loading && filteredData.length > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-xs text-muted-foreground">
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, filteredData.length)} of {filteredData.length}
              </div>
              <div className="flex items-center gap-2">
                <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-[70px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10" className="text-xs">10</SelectItem>
                    <SelectItem value="25" className="text-xs">25</SelectItem>
                    <SelectItem value="50" className="text-xs">50</SelectItem>
                    <SelectItem value="100" className="text-xs">100</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Prev
                  </Button>
                  <span className="px-2 text-xs flex items-center">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={page >= totalPages}
                    onClick={() => setPage(totalPages)}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Connections Modal */}
        <ConnectionsModal
          open={showConnectionsModal}
          onOpenChange={setShowConnectionsModal}
          connections={connections}
          onRefresh={fetchConnections}
        />

        {/* Save Preset Dialog */}
        <Dialog open={showSavePreset} onOpenChange={setShowSavePreset}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save Column Preset</DialogTitle>
              <DialogDescription>
                Save your current column configuration as a preset
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                placeholder="Preset name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                data-testid="preset-name-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSavePreset(false)}>
                Cancel
              </Button>
              <Button onClick={savePreset} data-testid="save-preset-btn">
                Save Preset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Campaign/Ad Detail Modal */}
        <Dialog open={!!selectedRow} onOpenChange={(open) => !open && setSelectedRow(null)}>
          <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedRow?.name}
                {selectedRow?.platform && (
                  <Badge variant="outline" className="text-xs">
                    {selectedRow.platform === 'meta' ? '📘 Meta' : selectedRow.platform === 'google' ? '🔵 Google' : selectedRow.platform}
                  </Badge>
                )}
                {selectedRow?.status && (
                  <Badge 
                    variant={selectedRow.status === 'active' ? 'default' : 'secondary'} 
                    className="text-xs cursor-pointer hover:opacity-80"
                    onClick={() => {
                      const newStatus = selectedRow.status === 'active' ? 'paused' : 'active';
                      axios.put(
                        `${API}/api/marketing/ads/campaigns/${selectedRow.id}`,
                        { status: newStatus },
                        { headers: { Authorization: `Bearer ${token}` } }
                      ).then(() => {
                        setSelectedRow(prev => ({ ...prev, status: newStatus }));
                        setData(prev => prev.map(r => r.id === selectedRow.id ? { ...r, status: newStatus } : r));
                        toast.success(`${newStatus === 'paused' ? 'Paused' : 'Activated'}`);
                      }).catch(() => toast.error('Failed'));
                    }}
                    title="Click to toggle"
                  >
                    {selectedRow.status}
                  </Badge>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <DialogDescription className="flex-1">
                  {level === 'campaign' ? 'Campaign' : level === 'adgroup' ? 'Ad Set' : 'Ad'} performance details
                  {hasData && connectionSummary.connected === 0 && (
                    <Badge variant="outline" className="ml-2 text-amber-500 border-amber-500 text-[10px]">Demo Data</Badge>
                  )}
                </DialogDescription>
                <Button
                  size="sm"
                  variant={selectedRow?.status === 'active' ? 'outline' : 'default'}
                  className={`text-xs gap-1 ${selectedRow?.status === 'active' ? 'text-yellow-600 border-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' : 'text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'}`}
                  onClick={() => {
                    if (!selectedRow) return;
                    const newStatus = selectedRow.status === 'active' ? 'paused' : 'active';
                    axios.put(
                      `${API}/api/marketing/ads/campaigns/${selectedRow.id}`,
                      { status: newStatus },
                      { headers: { Authorization: `Bearer ${token}` } }
                    ).then(() => {
                      setSelectedRow(prev => ({ ...prev, status: newStatus }));
                      setData(prev => prev.map(r => r.id === selectedRow.id ? { ...r, status: newStatus } : r));
                      toast.success(`${newStatus === 'paused' ? 'Paused' : 'Activated'}`);
                    }).catch(() => toast.error('Failed'));
                  }}
                >
                  {selectedRow?.status === 'active' ? <><Pause className="h-3 w-3" /> Pause</> : <><Play className="h-3 w-3" /> Activate</>}
                </Button>
              </div>
            </DialogHeader>
            
            {selectedRow && (
              <div className="space-y-6 py-2">
                {/* Performance KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Spend', value: selectedRow.spend, format: 'currency' },
                    { label: 'Impressions', value: selectedRow.impressions, format: 'number' },
                    { label: 'Clicks', value: selectedRow.clicks, format: 'number' },
                    { label: 'CTR', value: selectedRow.impressions > 0 ? (selectedRow.clicks / selectedRow.impressions * 100) : 0, format: 'percent' },
                    { label: 'Leads', value: selectedRow.leads, format: 'number' },
                    { label: 'CPA', value: selectedRow.cpa || (selectedRow.leads > 0 ? selectedRow.spend / selectedRow.leads : 0), format: 'currency' },
                    { label: 'ROAS', value: selectedRow.roas, format: 'roas' },
                    { label: 'Conversions', value: selectedRow.conversions || selectedRow.leads, format: 'number' },
                  ].map(kpi => (
                    <Card key={kpi.label} className="p-3">
                      <div className="text-xs text-muted-foreground">{kpi.label}</div>
                      <div className="text-lg font-bold mt-1">
                        {kpi.format === 'currency' ? `$${Number(kpi.value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` :
                         kpi.format === 'percent' ? `${Number(kpi.value || 0).toFixed(2)}%` :
                         kpi.format === 'roas' ? `${Number(kpi.value || 0).toFixed(2)}x` :
                         Number(kpi.value || 0).toLocaleString()}
                      </div>
                    </Card>
                  ))}
                </div>
                
                {/* Campaign Details */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Details</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedRow.objective && (
                      <div className="flex justify-between p-2 bg-muted/30 rounded">
                        <span className="text-muted-foreground">Objective</span>
                        <span className="font-medium capitalize">{selectedRow.objective}</span>
                      </div>
                    )}
                    {selectedRow.daily_budget && (
                      <div className="flex justify-between p-2 bg-muted/30 rounded">
                        <span className="text-muted-foreground">Daily Budget</span>
                        <span className="font-medium">${Number(selectedRow.daily_budget).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedRow.campaign_name && (
                      <div className="flex justify-between p-2 bg-muted/30 rounded">
                        <span className="text-muted-foreground">Campaign</span>
                        <span className="font-medium">{selectedRow.campaign_name}</span>
                      </div>
                    )}
                    {selectedRow.adset_name && (
                      <div className="flex justify-between p-2 bg-muted/30 rounded">
                        <span className="text-muted-foreground">Ad Set</span>
                        <span className="font-medium">{selectedRow.adset_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between p-2 bg-muted/30 rounded">
                      <span className="text-muted-foreground">Platform</span>
                      <span className="font-medium capitalize">{selectedRow.platform || '-'}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-muted/30 rounded">
                      <span className="text-muted-foreground">Status</span>
                      <Badge variant={selectedRow.status === 'active' ? 'default' : 'secondary'} className="text-xs capitalize">
                        {selectedRow.status || '-'}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Efficiency Analysis */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Efficiency vs Company Targets</h3>
                  <div className="space-y-2">
                    {[
                      { label: 'CPL', value: selectedRow.leads > 0 ? selectedRow.spend / selectedRow.leads : 0, target: 404.57, max: 484.94, format: '$', lowerBetter: true },
                      { label: 'ROAS', value: selectedRow.roas || 0, target: 6.66, min: 5.0, format: 'x', lowerBetter: false },
                      { label: 'CPA', value: selectedRow.cpa || 0, target: 909.81, max: 1189.64, format: '$', lowerBetter: true },
                    ].map(metric => {
                      const val = Number(metric.value || 0);
                      let status = 'on_track';
                      if (metric.lowerBetter) {
                        if (val > (metric.max || metric.target * 1.2)) status = 'below_min';
                        else if (val > metric.target) status = 'at_risk';
                      } else {
                        if (val < (metric.min || metric.target * 0.75)) status = 'below_min';
                        else if (val < metric.target) status = 'at_risk';
                      }
                      const colors = {
                        on_track: 'bg-green-500',
                        at_risk: 'bg-yellow-500',
                        below_min: 'bg-red-500'
                      };
                      // Bar shows actual relative to target
                      // For lower-is-better: bar = actual/target (small bar = good)
                      // For higher-is-better: bar = actual/target capped at 100% (full bar = good)
                      let pct;
                      if (metric.lowerBetter) {
                        pct = metric.target > 0 ? Math.min(100, (val / metric.target) * 100) : 0;
                      } else {
                        pct = metric.target > 0 ? Math.min(100, (val / metric.target) * 100) : 0;
                      }
                      
                      return (
                        <div key={metric.label} className="flex items-center gap-3">
                          <span className="text-sm w-12 text-muted-foreground">{metric.label}</span>
                          <div className="flex-1 h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${colors[status]}`} style={{width: `${pct}%`}} />
                          </div>
                          <span className="text-sm font-medium w-20 text-right">
                            {metric.format === '$' ? `$${val.toFixed(2)}` : `${val.toFixed(2)}${metric.format}`}
                          </span>
                          <span className="text-xs text-muted-foreground w-24 text-right">
                            Target: {metric.format === '$' ? `$${metric.target}` : `${metric.target}${metric.format}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Child Ads/Ad Sets within this campaign */}
                {selectedRow.child_count > 0 && (
                  <CampaignChildrenSection 
                    campaignId={selectedRow.id} 
                    campaignName={selectedRow.name}
                    childLevel={level === 'campaign' ? 'adgroup' : 'ad'}
                    token={token}
                    dateRange={dateRange}
                    onViewChild={(child) => setSelectedRow(child)}
                  />
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedRow(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Campaign Dialog */}
        <Dialog open={showCreateCampaign} onOpenChange={setShowCreateCampaign}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create New Campaign</DialogTitle>
              <DialogDescription>
                Create a campaign in demo mode. In production, this creates a campaign on the connected ad platform.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Campaign Name</Label>
                <Input
                  placeholder="e.g., SSW | JACKSONVILLE | Q1"
                  value={newCampaignData.name}
                  onChange={(e) => setNewCampaignData(p => ({ ...p, name: e.target.value }))}
                  data-testid="create-campaign-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select value={newCampaignData.platform} onValueChange={(v) => setNewCampaignData(p => ({ ...p, platform: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meta">📘 Meta (Facebook/IG)</SelectItem>
                      <SelectItem value="google">🔵 Google Ads</SelectItem>
                      <SelectItem value="tiktok">🎵 TikTok</SelectItem>
                      <SelectItem value="youtube">▶️ YouTube</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Objective</Label>
                  <Select value={newCampaignData.objective} onValueChange={(v) => setNewCampaignData(p => ({ ...p, objective: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="leads">Leads</SelectItem>
                      <SelectItem value="conversions">Conversions</SelectItem>
                      <SelectItem value="awareness">Awareness</SelectItem>
                      <SelectItem value="traffic">Traffic</SelectItem>
                      <SelectItem value="engagement">Engagement</SelectItem>
                      <SelectItem value="video_views">Video Views</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Daily Budget ($)</Label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={newCampaignData.daily_budget}
                    onChange={(e) => setNewCampaignData(p => ({ ...p, daily_budget: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Market</Label>
                  <Select value={newCampaignData.market || 'none'} onValueChange={(v) => setNewCampaignData(p => ({ ...p, market: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select market" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Markets</SelectItem>
                      <SelectItem value="jacksonville">Jacksonville</SelectItem>
                      <SelectItem value="orlando">Orlando</SelectItem>
                      <SelectItem value="tampa">Tampa</SelectItem>
                      <SelectItem value="west-palm">West Palm</SelectItem>
                      <SelectItem value="naples">Naples</SelectItem>
                      <SelectItem value="gainesville">Gainesville</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateCampaign(false)}>Cancel</Button>
              <Button onClick={handleCreateCampaign} disabled={!newCampaignData.name.trim()}>
                <Plus className="h-4 w-4 mr-1" />
                Create Campaign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdsManager;
